/**
 * Restaurant Menu & Ordering Types
 * Aligned with Firestore Data Model (multi-tenant)
 */

import type { Language } from '../../config';

export type LocalizedString = Partial<Record<Language, string>> & { th: string; en: string };

export type DietTag = 'veg' | 'spicy' | 'gf';
export type OrderMode = 'room' | 'dine' | 'takeaway';
export type PaymentMode = 'room' | 'tbl';
export type OrderStatus = 'received' | 'preparing' | 'on_the_way' | 'delivered' | 'cancelled';

export const SERVICE_CHARGE_PCT = 10;

export interface OptionChoice {
  id: string;
  name: LocalizedString;
  priceDelta: number;
}

export interface OptionGroup {
  id: string;
  name: LocalizedString;
  required: boolean;
  min: number;
  max: number;
  choices: OptionChoice[];
}

/** Matches Firestore: .../outlets/{outletId}/items/{itemId} */
export interface MenuItem {
  id: string;
  categoryId: string;
  name: LocalizedString;
  desc?: LocalizedString;
  description?: LocalizedString;
  price: number;
  imageUrl?: string;
  tags: DietTag[];
  allergens: string[];
  available: boolean;
  sort: number;
  optionGroups: OptionGroup[];
}

/** Matches Firestore: .../outlets/{outletId}/categories/{catId} */
export interface MenuCategory {
  id: string;
  name: LocalizedString;
  sort: number;
  active: boolean;
  items: MenuItem[];
}

/** Cart item in UI state */
export interface CartItem {
  item: MenuItem;
  qty: number;
  note: string;
  selectedOptions: Record<string, string>; // optionGroupId -> choiceId
}

/** Snapshot stored in order.lines[] */
export interface OrderLine {
  itemId: string;
  nameSnapshot: LocalizedString;
  priceSnapshot: number;
  qty: number;
  options: Record<string, string>;
  note: string;
}

/** Draft order built client-side (totals computed server-side on submit) */
export interface DraftOrder {
  outletId: string;
  roomNo?: string;
  tableNo?: string;
  mode: OrderMode;
  payment: PaymentMode;
  lines: OrderLine[];
  subtotal: number;
  serviceChargePct: number;
  serviceCharge: number;
  total: number;
}
